import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles, ChevronDown, ChevronUp, ArrowRight, Loader2,
  Users, Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "How This Studio Works" — explains the persona handoff flow.
 * Collapsible by default to stay out of the way.
 */
export const HowItWorks = () => {
  const [open, setOpen] = useState(false);
  const [personas, setPersonas] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/studio/personas`).then(r => {
      if (mounted) setPersonas(r.data.personas || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <div className={`sf-how-it-works ${open ? 'open' : ''}`} data-testid="how-it-works">
      <button
        type="button"
        className="sf-how-toggle"
        onClick={() => setOpen(o => !o)}
        data-testid="how-it-works-toggle"
      >
        <span className="sf-how-toggle-left">
          <Flame size={16} className="sf-how-flame" />
          <span className="sf-how-title">How this studio works</span>
          <span className="sf-how-sub">9 specialists. One manuscript. Real handoffs.</span>
        </span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {open && (
        <div className="sf-how-body">
          <p className="sf-how-intro">
            Scribe Flow isn&apos;t a single AI chatbot — it&apos;s a <strong>studio of named specialists</strong>,
            each with their own voice and their own job. You walk in with a manuscript, lyrics,
            a poem, or a song idea. Each room introduces you to its specialist. When you&apos;re ready
            to move on, your work is <strong>handed off</strong> to the next room with notes —
            just like working with a real team of editors, designers, and producers.
          </p>

          <div className="sf-how-flow">
            <h4>The flow</h4>
            <ol>
              <li><strong>You stay in control.</strong> You can visit rooms in any order, skip rooms, or revisit anytime.</li>
              <li><strong>Each specialist introduces themselves</strong> the first time you chat with them.</li>
              <li><strong>Long manuscripts are chunked automatically.</strong> Aiden (the rewriter) works chapter by chapter — say <em>&ldquo;continue&rdquo;</em> or <em>&ldquo;next&rdquo;</em> to move forward.</li>
              <li><strong>When a room is done</strong>, the specialist hands your work to the next one with a short summary. You see the handoff happen live.</li>
              <li><strong>Premium tools (audiobook, cover art, translation)</strong> use your OpenAI API key — you pay OpenAI directly, never us.</li>
            </ol>
          </div>

          {personas.length > 0 && (
            <div className="sf-how-team">
              <h4>Meet the specialists</h4>
              <div className="sf-team-grid">
                {personas.map((p) => (
                  <div key={p.module_id} className="sf-team-card" data-testid={`team-${p.module_id}`}>
                    <div className="sf-team-avatar">{p.name.charAt(0)}</div>
                    <div className="sf-team-text">
                      <div className="sf-team-name">{p.name}</div>
                      <div className="sf-team-role">{p.title}</div>
                      <div className="sf-team-module">{p.module_label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="sf-how-quiet">
            <p>
              <strong>One final note:</strong> your manuscript stays in this browser session.
              Nothing is logged to any database unless you save it explicitly. Your API key
              (if you use one) is kept only in memory for the duration of your work. Your story
              belongs to you.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * <HandoffCard> — render the persona-to-persona handoff dialogue and
 * a CTA to open the next module.
 *
 * Props: fromModule, manuscriptSummary, onAccept(nextModule)
 */
export const HandoffCard = ({ fromModule, manuscriptSummary = "", onAccept }) => {
  const [loading, setLoading] = useState(true);
  const [dialogue, setDialogue] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    axios.post(`${API}/studio/handoff`, {
      from_module: fromModule,
      manuscript_summary: manuscriptSummary,
    }).then(r => {
      if (mounted) setDialogue(r.data);
    }).catch(e => {
      if (mounted) setError("Could not load the handoff.");
      console.error(e);
    }).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [fromModule, manuscriptSummary]);

  if (loading) {
    return (
      <div className="sf-handoff-card sf-handoff-loading" data-testid="handoff-loading">
        <Loader2 className="spinning" size={18} /> Preparing the handoff...
      </div>
    );
  }
  if (error || !dialogue) {
    return null;
  }

  const hasNext = !!dialogue.next_module;

  return (
    <div className="sf-handoff-card" data-testid="handoff-card">
      <div className="sf-handoff-header">
        <Users size={16} />
        <span>Handoff</span>
      </div>

      {dialogue.outgoing && (
        <div className="sf-handoff-msg sf-handoff-out">
          <div className="sf-handoff-msg-author">
            <span className="sf-handoff-avatar sf-handoff-avatar-out">{dialogue.outgoing_name?.charAt(0)}</span>
            <span>
              <strong>{dialogue.outgoing_name}</strong>
              <span className="sf-handoff-role">{dialogue.outgoing_title}</span>
            </span>
          </div>
          <p>{dialogue.outgoing}</p>
        </div>
      )}

      {dialogue.incoming && (
        <div className="sf-handoff-msg sf-handoff-in">
          <div className="sf-handoff-msg-author">
            <span className="sf-handoff-avatar sf-handoff-avatar-in">{dialogue.incoming_name?.charAt(0)}</span>
            <span>
              <strong>{dialogue.incoming_name}</strong>
              <span className="sf-handoff-role">{dialogue.incoming_title}</span>
            </span>
          </div>
          <p>{dialogue.incoming}</p>
        </div>
      )}

      {hasNext && (
        <button
          type="button"
          className="sf-handoff-cta"
          onClick={() => onAccept && onAccept(dialogue.next_module)}
          data-testid="handoff-accept"
        >
          Open the next room with {dialogue.incoming_name} <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
};

export default HowItWorks;
